import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import {
  addConstraint,
  addPoint,
  constrainToBounds,
  createSystem,
  integrate,
  solve,
  type VerletSystem,
} from '../../core/verlet';
import type { RenderTarget, TechniqueContext } from '../types';
import type { SoftBodyValues } from './params';

/**
 * The letters as a spring-mass mesh that sags, stretches and sways.
 *
 * Mass points are placed on a lattice clipped to the glyph interior, then connected to
 * their right and lower neighbours (structural springs) and diagonally (shear springs).
 * Without the diagonals a quad lattice has no resistance to shearing at all and the
 * letters collapse into parallelograms on the first frame.
 */
export interface SoftBodyState {
  system: VerletSystem;
  /** Rest lengths, kept separately so the draw pass can colour by stretch. */
  restLengths: Float32Array;
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
  originY: Float32Array;
}

function values(ctx: TechniqueContext): SoftBodyValues {
  return ctx.values as unknown as SoftBodyValues;
}

export function init(ctx: TechniqueContext): SoftBodyState {
  const v = values(ctx);
  const spacing = Math.max(2, v.meshSpacing);
  const bounds = ctx.field.bounds;

  const cols = Math.max(1, Math.ceil(bounds.width / spacing) + 1);
  const rows = Math.max(1, Math.ceil(bounds.height / spacing) + 1);
  // Cap the mesh so a small spacing on a large canvas cannot allocate unboundedly.
  const capacity = Math.min(120000, cols * rows);
  const system = createSystem(capacity, capacity * 4);

  // Lattice index → point id, or -1 where the lattice falls outside the glyphs.
  const grid = new Int32Array(cols * rows).fill(-1);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (system.count >= capacity) break;
      const x = bounds.x + c * spacing;
      const y = bounds.y + r * spacing;
      if (!ctx.field.isInside(x, y)) continue;

      let inverseMass = 1;
      switch (v.anchors) {
        case 'top':
          if (r === 0 || !ctx.field.isInside(x, y - spacing)) inverseMass = 0;
          break;
        case 'bottom':
          if (r === rows - 1 || !ctx.field.isInside(x, y + spacing)) inverseMass = 0;
          break;
        case 'sides':
          if (!ctx.field.isInside(x - spacing, y) || !ctx.field.isInside(x + spacing, y)) inverseMass = 0;
          break;
        case 'corners':
          if (
            (r === 0 || !ctx.field.isInside(x, y - spacing)) &&
            (!ctx.field.isInside(x - spacing, y) || !ctx.field.isInside(x + spacing, y))
          ) {
            inverseMass = 0;
          }
          break;
        default:
          inverseMass = 1;
      }

      grid[r * cols + c] = addPoint(system, x, y, inverseMass);
    }
  }

  const link = (a: number, b: number, stiffness: number): void => {
    if (a < 0 || b < 0) return;
    addConstraint(system, a, b, stiffness);
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const id = grid[r * cols + c];
      if (id < 0) continue;
      const right = c + 1 < cols ? grid[r * cols + c + 1] : -1;
      const down = r + 1 < rows ? grid[(r + 1) * cols + c] : -1;
      const diagA = r + 1 < rows && c + 1 < cols ? grid[(r + 1) * cols + c + 1] : -1;
      const diagB = r + 1 < rows && c > 0 ? grid[(r + 1) * cols + c - 1] : -1;

      link(id, right, v.stiffness);
      link(id, down, v.stiffness);
      // Shear springs at reduced stiffness: full stiffness makes the mesh rigid.
      link(id, diagA, v.stiffness * 0.55);
      link(id, diagB, v.stiffness * 0.55);
    }
  }

  const originY = new Float32Array(system.count);
  originY.set(system.y.subarray(0, system.count));

  return {
    system,
    restLengths: Float32Array.from(system.rest.subarray(0, system.constraintCount)),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    stepsDone: 0,
    targetSteps: v.steps,
    originY,
  };
}

export function step(state: SoftBodyState, _dt: number, ctx: TechniqueContext): SoftBodyState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);
  const perFrame = Math.max(1, Math.round(3 * clamp01(ctx.quality)));

  for (let f = 0; f < perFrame && state.stepsDone < state.targetSteps; f++) {
    // Wind as a slow oscillation rather than a constant, so the body sways.
    const t = state.stepsDone * 0.05 * v.windFrequency;
    const wind = Math.sin(t) * v.wind * 0.35 + Math.sin(t * 2.3) * v.wind * 0.15;

    integrate(state.system, {
      gravityX: 0,
      gravityY: v.gravity,
      damping: v.damping,
      windX: wind,
      windY: 0,
    });
    solve(state.system, v.iterations);
    constrainToBounds(state.system, ctx.width, ctx.height, 0.3);
    state.stepsDone++;
  }

  return state;
}

export function isComplete(state: SoftBodyState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: SoftBodyState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const { system } = state;
  c.lineWidth = v.lineWidth;
  c.lineCap = 'round';

  if (v.style === 'points') {
    const buckets = new Map<string, Path2D>();
    for (let i = 0; i < system.count; i++) {
      const t = v.colorMode === 'height' ? clamp01(system.y[i] / ctx.height) : 0;
      const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
      let path = buckets.get(color);
      if (!path) {
        path = new Path2D();
        buckets.set(color, path);
      }
      path.rect(system.x[i] - v.lineWidth, system.y[i] - v.lineWidth, v.lineWidth * 2, v.lineWidth * 2);
    }
    for (const [color, path] of buckets) {
      c.fillStyle = color;
      c.fill(path);
    }
    return;
  }

  // Mesh and blob both draw the constraints; blob simply strokes them fat enough to
  // merge into a surface, which reads as a deformed letterform without needing a
  // triangulation of the deformed mesh.
  if (v.style === 'blob') c.lineWidth = Math.max(v.lineWidth, v.meshSpacing * 1.15);

  const buckets = new Map<string, Path2D>();
  for (let k = 0; k < system.constraintCount; k++) {
    const a = system.ca[k];
    const b = system.cb[k];
    const dx = system.x[b] - system.x[a];
    const dy = system.y[b] - system.y[a];

    let t: number;
    if (v.colorMode === 'stretch') {
      const len = Math.hypot(dx, dy);
      const rest = state.restLengths[k] || 1;
      t = clamp01(Math.abs(len - rest) / (rest * 0.6));
    } else if (v.colorMode === 'height') {
      t = clamp01((system.y[a] + system.y[b]) / 2 / ctx.height);
    } else {
      t = 0;
    }

    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    let path = buckets.get(color);
    if (!path) {
      path = new Path2D();
      buckets.set(color, path);
    }
    path.moveTo(system.x[a], system.y[a]);
    path.lineTo(system.x[b], system.y[b]);
  }

  for (const [color, path] of buckets) {
    c.strokeStyle = color;
    c.stroke(path);
  }
}

export function elementCount(state: SoftBodyState): number {
  return state.system.count;
}
